import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// import { DynamicSeo } from './seo/SeoProvider'; // DESACTIVADO: Causa duplicados de metas
import './i18n';
import { useTranslation } from 'react-i18next';
import { PUBLIC_BOOKING_ENABLED } from './config';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import ScrollManager from './components/common/ScrollManager';
import CookieBanner from './components/cookies/CookieBanner';
import PublicDocument from './pages/public/PublicDocument';
import useCookieConsent from './hooks/useCookieConsent';
import { trackPageView } from './utils/visitorTracking';
import './index.css';

// Páginas públicas críticas
import Landing from './pages/Landing';
import ContactHubPage from './pages/ContactHubPage';
import ContactoPage from './pages/ContactoPage';
import CallbackPage from './pages/CallbackPage';
import ContactWizardPage from './pages/ContactWizardPage';
import PlataformaPage from './pages/PlataformaPage';
import InteroperabilidadPage from './pages/InteroperabilidadPage';

import GuiasPage from './pages/GuiasPage';
import QuienesSomosPage from './pages/QuienesSomosPage';

import DiferenciasDocumentosPage from './pages/DiferenciasDocumentosPage';

import FormatoPDFA1APage from './pages/FormatoPDFA1APage';
import DigitalizacionTransportePiezaPage from './pages/DigitalizacionTransportePiezaPage';
import QueEsElDecaPage from './pages/QueEsElDecaPage';
import PrivacidadPage from './pages/PrivacidadPage';
import CookiesPage from './pages/CookiesPage';
import TerminosPage from './pages/TerminosPage';
import TermsPageEn from './pages/TermsPageEn';
import PrivacyPageEn from './pages/PrivacyPageEn';
import AvisoLegalPage from './pages/AvisoLegalPage';
import DPAPage from './pages/DPAPage';
import PruebaGratisPage from './pages/PruebaGratisPage';
import GooglePlaySecurityPage from './pages/GooglePlaySecurityPage';
import AppStoreSecurityPage from './pages/AppStoreSecurityPage';



import CumplimientoNormativoPage from './pages/CumplimientoNormativoPage';
import SeguridadPage from './pages/SeguridadPage';
import FirmaElectronicaConfianzaPage from './pages/FirmaElectronicaConfianzaPage';

import DocumentoControlPage from './pages/DocumentoControlPage';
import ECMRDocumentoPage from './pages/ECMRDocumentoPage';
import CartaPorteDocumentoPage from './pages/CartaPorteDocumentoPage';
import TransporteADRDocumentoPage from './pages/TransporteADRDocumentoPage';
import DocumentoControlDigital2026 from './pages/landing/DocumentoControlDigital2026';

import BookingPage from './pages/BookingPage';
import MeetingPage from './pages/MeetingPage';
import TransportistasPage from './pages/TransportistasPage';
import CargadoresPage from './pages/CargadoresPage';
import ProveedoresSoftwarePage from './pages/ProveedoresSoftwarePage';

import LanguageWrapper from './components/common/LanguageWrapper';
import DesarrolladoresPage from './pages/DesarrolladoresPage';
import ChatWidget from './components/chat/ChatWidget';
import { ChatProvider } from './components/chat/ChatContext';
import NotFoundPage from './pages/NotFoundPage';

import PrerenderIndexPage from './pages/PrerenderIndexPage';
// import MetaTags from './components/common/MetaTags'; // COMENTADO: Interfiere con SeoMeta

// El hreflang ya NO se gestiona aquí: useHreflang borraba TODOS los
// link[rel="alternate"] del head (incluidos los de Helmet) y reinyectaba los
// suyos fuera de Helmet — la misma carrera que dejó canonicals duplicados con
// useCanonicalUrl. Ahora cada página con variante de idioma declara sus
// `alternates` vía SeoMeta (/, /plataforma, /interoperabilidad, /contacto,
// /documentos/ecmr), y las páginas de un solo idioma no emiten hreflang.

// Componente para rastrear visitas de página
const VisitorTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Skip durante pre-rendering
    if (navigator.userAgent === 'ReactSnap' || window.__PRERENDER_INJECTED) return;
    
    // Track page view on route change
    const pageUrl = window.location.href;
    const pageTitle = document.title;
    
    trackPageView(pageUrl, pageTitle);
  }, [location.pathname, location.search]);

  return null;
};



function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ChatProvider>
        <VisitorTracker />
        <ScrollManager />
        <CookieBanner />
        <div className="App min-h-screen flex flex-col">
            <Routes>
            {/* Landing de conversión — redirige al wizard con origen */}
            <Route path="/landing/documento-control-digital-2026" element={
              <Navigate to="/contacto?origin=dca-2026" replace />
            } />
            
            {/* Landing */}
            <Route path="/" element={
              <>
                <a href="/__prerender" style={{display:'none'}} aria-hidden="true">prerender</a>
                <Header />
                <main className="flex-grow">
                  <Landing />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/__prerender" element={<PrerenderIndexPage />} />
            
            {/* Páginas comerciales */}
            {/* Blog antiguo: sin enlaces ni sitemap; retirado el 4-nov-2026 */}
            <Route path="/blog" element={<Navigate to="/articulos" replace />} />
            
            <Route path="/blog/:id" element={<Navigate to="/articulos" replace />} />
            
            {/* Contacto: tres vías (demo con cualificación, formulario, llamada).
                Los ?origin=…&profile=… de los enlaces internos pasan de la
                página de contacto a cada vía. */}
            <Route path="/contacto" element={
              <>
                <Header />
                <main className="flex-grow">
                  <ContactHubPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/contacto/demo" element={PUBLIC_BOOKING_ENABLED ? (
              <>
                <Header />
                <main className="flex-grow">
                  <ContactWizardPage />
                </main>
                <Footer />
              </>
            ) : (
              <Navigate to="/contacto/formulario" replace />
            )} />

            <Route path="/contacto/formulario" element={
              <>
                <Header />
                <main className="flex-grow">
                  <ContactoPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/contacto/llamada" element={
              <>
                <Header />
                <main className="flex-grow">
                  <CallbackPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/contacto-directo" element={
              <Navigate to="/contacto/formulario" replace />
            } />
            
            <Route path="/plataforma" element={
              <>
                <Header />
                <main className="flex-grow">
                  <PlataformaPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/interoperabilidad" element={
              <>
                <Header />
                <main className="flex-grow">
                  <InteroperabilidadPage />
                </main>
                <Footer />
              </>
            } />
            


            <Route path="/confianza/cumplimiento-normativo" element={
              <>
                <Header />
                <main className="flex-grow">
                  <CumplimientoNormativoPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/confianza/seguridad" element={
              <>
                <Header />
                <main className="flex-grow">
                  <SeguridadPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/confianza/firma-electronica" element={
              <>
                <Header />
                <main className="flex-grow">
                  <FirmaElectronicaConfianzaPage />
                </main>
                <Footer />
              </>
            } />
            
            {/* Conservación de evidencias unida a Firma (4-nov-2026); 301 en cloudfront-function-final.js */}
            <Route path="/confianza/conservacion-evidencias" element={<Navigate to="/confianza/firma-electronica" replace />} />
            

            
            <Route path="/articulos" element={
              <>
                <Header />
                <main className="flex-grow">
                  <GuiasPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/quienes-somos" element={
              <>
                <Header />
                <main className="flex-grow">
                  <QuienesSomosPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/:lang/about-us" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <QuienesSomosPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr" element={
              <>
                <Header />
                <main className="flex-grow">
                  <DiferenciasDocumentosPage />
                </main>
                <Footer />
              </>
            } />
            
            {/* Artículo «Qué es eFTI»: fusionado en el del calendario eFTI (4-nov-2026) */}
            <Route path="/articulos/que-es-efti-y-como-impactara-la-documentacion-de-transporte" element={<Navigate to="/articulos/digitalizacion-documento-control-transporte-datos-efti" replace />} />
            
            {/* Artículo de firma eIDAS: repetía Confianza › Firma (4-nov-2026) */}
            <Route path="/articulos/firma-electronica-simple-vs-avanzada-que-exige-eidas" element={<Navigate to="/confianza/firma-electronica" replace />} />
            
            {/* Artículo «Ventajas de digitalizar»: lo cuentan Plataforma y Soluciones (4-nov-2026) */}
            <Route path="/articulos/ventajas-de-digitalizar-la-carta-de-porte-y-el-ecmr" element={<Navigate to="/plataforma" replace />} />
            
            <Route path="/articulos/formato-pdfa1a-documento-control-digital-2026" element={
              <>
                <Header />
                <main className="flex-grow">
                  <FormatoPDFA1APage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/articulos/digitalizacion-documento-control-transporte-datos-efti" element={
              <>
                <Header />
                <main className="flex-grow">
                  <DigitalizacionTransportePiezaPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/articulos/que-es-el-deca" element={
              <>
                <Header />
                <main className="flex-grow">
                  <QueEsElDecaPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/articulos/que-es-el-documento-de-control-administrativo-digital-dca" element={
              <Navigate to="/documentos/documento-control" replace />
            } />
            
            <Route path="/articulos/que-es-el-documento-de-control-administrativo-obligatorio-digital" element={
              <Navigate to="/documentos/documento-control" replace />
            } />
            
            <Route path="/articulos/ecmr-todo-lo-que-necesitas-saber" element={
              <Navigate to="/documentos/ecmr" replace />
            } />
            
            

            
            {/* Categorías de artículos: con 5 artículos se quedaban vacías; el índice ya agrupa (4-nov-2026) */}
            <Route path="/articulos/categoria/:categoria" element={<Navigate to="/articulos" replace />} />
            
            <Route path="/privacidad" element={
              <>
                <Header />
                <main className="flex-grow">
                  <PrivacidadPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/terminos" element={
              <>
                <Header />
                <main className="flex-grow">
                  <TerminosPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/aviso-legal" element={
              <>
                <Header />
                <main className="flex-grow">
                  <AvisoLegalPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/dpa" element={
              <>
                <Header />
                <main className="flex-grow">
                  <DPAPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/google-play-security" element={
              <>
                <Header />
                <main className="flex-grow">
                  <GooglePlaySecurityPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/app-store-security" element={
              <>
                <Header />
                <main className="flex-grow">
                  <AppStoreSecurityPage />
                </main>
                <Footer />
              </>
            } />


            
            <Route path="/cookies" element={
              <>
                <Header />
                <main className="flex-grow">
                  <CookiesPage />
                </main>
                <Footer />
              </>
            } />
            
            {/* /demo lleva a la reserva con cualificación (perfil + volumen);
                con las reservas apagadas, /contacto/demo ya redirige al formulario. */}
            <Route path="/demo" element={<Navigate to="/contacto/demo?origin=demo" replace />} />

            <Route path="/periodo-prueba" element={
              <Navigate to="/contacto/demo?origin=demo" replace />
            } />

            {/* /desarrolladores: página mínima sin enlaces; el portal está en /es/developers/info (4-nov-2026) */}
            <Route path="/desarrolladores" element={<Navigate to="/proveedores-software" replace />} />
            
            {/* /precios era el modelo legacy (licencia + uso mensual); retirada 9-sep-2026 */}
            <Route path="/precios" element={<Navigate to="/" replace />} />

            <Route path="/transportistas" element={
              <>
                <Header />
                <main className="flex-grow">
                  <TransportistasPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/cargadores" element={
              <>
                <Header />
                <main className="flex-grow">
                  <CargadoresPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/proveedores-software" element={
              <>
                <Header />
                <main className="flex-grow">
                  <ProveedoresSoftwarePage />
                </main>
                <Footer />
              </>
            } />
            

            <Route path="/documentos/deca" element={<Navigate to="/documentos/documento-control" replace />} />
            
            <Route path="/documentos/documento-control" element={
              <>
                <Header />
                <main className="flex-grow">
                  <DocumentoControlPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/documentos/ecmr" element={
              <>
                <Header />
                <main className="flex-grow">
                  <ECMRDocumentoPage />
                </main>
                <Footer />
              </>
            } />
            
            <Route path="/documentos/carta-porte" element={
              <>
                <Header />
                <main className="flex-grow">
                  <CartaPorteDocumentoPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/documentos/transporte-adr" element={
              <>
                <Header />
                <main className="flex-grow">
                  <TransporteADRDocumentoPage />
                </main>
                <Footer />
              </>
            } />

            {/* Rutas multiidioma */}
            <Route path="/:lang" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <Landing />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/demo" element={<Navigate to="/en/contact/demo?origin=demo" replace />} />

            <Route path="/:lang/trial" element={
              <Navigate to="/en/contact/demo?origin=demo" replace />
            } />

            <Route path="/:lang/essai" element={
              <Navigate to="/contacto?origin=demo" replace />
            } />
            
            <Route path="/:lang/pricing" element={<Navigate to="/" replace />} />
            

            
            <Route path="/:lang/contact" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <ContactHubPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/contact/demo" element={PUBLIC_BOOKING_ENABLED ? (
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <ContactWizardPage />
                </main>
                <Footer />
              </LanguageWrapper>
            ) : (
              <Navigate to="/en/contact/form" replace />
            )} />

            <Route path="/:lang/contact/form" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <ContactoPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/contact/call" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <CallbackPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/developers" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <DesarrolladoresPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/platform" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <PlataformaPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/interoperability" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <InteroperabilidadPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/trust/regulatory-compliance" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <CumplimientoNormativoPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/trust/security" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <SeguridadPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/trust/electronic-signature" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <FirmaElectronicaConfianzaPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/trust/evidence-preservation" element={<Navigate to="/en/trust/electronic-signature" replace />} />


            
            {/* Artículos solo en español (4-nov-2026): /en/articles lleva a la home inglesa (301 en CloudFront) */}
            <Route path="/:lang/articles" element={<Navigate to="/en" replace />} />
            



            

            



            
            <Route path="/:lang/terms" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <TermsPageEn />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/terminos" element={<Navigate to="/en/terms" replace />} />

            <Route path="/:lang/legal-notice" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <AvisoLegalPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/dpa" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <DPAPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            

            
            {/* Versión inglesa ampliada (4-nov-2026): soluciones, DeCA, ADR y legales */}
            <Route path="/:lang/documents/control-document" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <DocumentoControlPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/documents/adr-transport-document" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <TransporteADRDocumentoPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/shippers" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <CargadoresPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/carriers" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <TransportistasPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/software-providers" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <ProveedoresSoftwarePage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/privacy" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <PrivacyPageEn />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            <Route path="/:lang/cookies" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <CookiesPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />

            <Route path="/:lang/documents/road-waybill" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <CartaPorteDocumentoPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            <Route path="/:lang/documents/ecmr" element={
              <LanguageWrapper>
                <Header />
                <main className="flex-grow">
                  <ECMRDocumentoPage />
                </main>
                <Footer />
              </LanguageWrapper>
            } />
            
            {/* Guía de cumplimiento (lead magnet): retirada el 4-nov-2026 («ya ha
                pasado ese momento del DeCA»); la URL lleva a la página del DeCA. */}
            <Route path="/guias/cumplimiento-documento-control-2026" element={<Navigate to="/documentos/documento-control" replace />} />

            {/* Guía «DeCA obligatorio»: retirada el 4-nov-2026, como la anterior; sus
                URLs llevan a la página del DeCA. */}
            <Route path="/guias/documento-control-digital-obligatorio" element={<Navigate to="/documentos/documento-control" replace />} />
            <Route path="/articulos/documento-control-digital-obligatorio" element={<Navigate to="/documentos/documento-control" replace />} />

            {/* Booking — public appointment scheduling */}
            <Route path="/booking/:token" element={
              <>
                <Header />
                <main className="flex-grow">
                  <BookingPage />
                </main>
                <Footer />
              </>
            } />

            {/* Meeting — generic booking with hidden token */}
            <Route path="/meeting" element={
              <>
                <Header />
                <main className="flex-grow">
                  <MeetingPage />
                </main>
                <Footer />
              </>
            } />

            <Route path="/doc/:token" element={<PublicDocument />} />

            {/* Asesor de IA retirado (4-nov-2026, Jesús: «ahora mismo no es necesaria»); 301 en cloudfront-function-final.js */}
            <Route path="/asesor-ia" element={<Navigate to="/articulos" replace />} />

            {/* 404 — debe ir la última. Marca noindex para que las URLs
                inexistentes dejen de indexarse como duplicados de la home. */}
            <Route path="*" element={
              <>
                <Header />
                <main className="flex-grow">
                  <NotFoundPage />
                </main>
                <Footer />
              </>
            } />
          </Routes>
          <ChatWidgetConditional />
        </div>
      </ChatProvider>
      </Router>
  );
}

function ChatWidgetConditional() {
  return null;
}

export default App;
